//
//  commonGuides.ts
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module builds the guides of pages that recur for many records: notes, email
//    recipients, data entry and item versions.
//

import type { Permission } from "src/api/Tmd.dtos";

import {
    builderTourTargets,
    emailTourTargets,
    entryTourTargets,
    helpTourTargets,
    insertionTourTarget
} from "../helpTourTargets";
import {
    actionsStop,
    column,
    createGuide,
    field,
    filterStop,
    grid,
    item,
    menu,
    saveStop,
    stop,
    type PageGuide,
    type PageGuideStop
} from "./pageGuide";

/** The pages of a record's notes: the list, one note, and a new note. */
export const notesGuides = (
    owner: string,
    createPermission: Permission,
    updatePermission: Permission
) => {
    const note: PageGuide = {
        intro: `One note about ${owner}. Notes keep correspondence, decisions and anything else worth recording against ${owner}, with who wrote them and when.`,
        stops: [
            field(
                "note.title",
                "Title",
                "A one-line summary, shown in the notes list, so the note can be found again without opening it."
            ),
            field(
                "note.noteTypeId",
                "Note Type",
                "The kind of note, such as correspondence or a meeting. Note types are maintained in settings and help group notes in the list."
            ),
            field(
                "note.comments",
                "Comments",
                "The note itself. Record what happened, who was involved and any follow-up needed."
            ),
            field(
                "note.active",
                "Active",
                "Clear to hide the note from the list. It is kept, and can be shown again by switching on inactive records in the list."
            ),
            saveStop(
                "Submit saves the note and records you as its author. Submit And Return also goes back to the notes list.",
                updatePermission
            )
        ],
        rules: ["Title and Note Type are required."]
    };

    return {
        list: {
            intro: `Notes recorded about ${owner}. Select a column heading to sort by it.`,
            stops: [
                filterStop(
                    "Type part of a title or the note text to filter the list. Switch on inactive records to include notes that have been hidden."
                ),
                actionsStop(
                    `Add opens a blank note about ${owner}. Nothing is saved until you submit it.`,
                    createPermission
                ),
                grid("Select a note to read it in full or change it.", [
                    column(
                        "Title",
                        "The note's one-line summary; open the note to read it in full."
                    ),
                    column(
                        "Comments",
                        "The start of the note text; open the note to read the rest."
                    ),
                    column(
                        "Date Entered",
                        "When the note was recorded; sort by this column to see the latest notes first."
                    )
                ])
            ]
        } satisfies PageGuide,
        item: note,
        create: createGuide(
            note,
            `Record a new note about ${owner}. Give it a title and type, then write the note.`,
            ["The note appears at the top of the notes list once saved, with you as its author."],
            { omit: ["note.active"] }
        )
    };
};

/** A level at which email recipients can be set, from most general to most specific. */
export type EmailLevel =
    | "global"
    | "trial"
    | "site"
    | "participant"
    | "caseReport"
    | "institution"
    | "record";

const recipientStatus: Record<EmailLevel, string> = {
    global: 'The recipients every trial starts from. "Recipients · 1 to, 2 cc" counts them by To, Cc and Bcc; "Not sent" switches the email off unless a trial, site or participant turns it back on. Hover to see where each recipient comes from.',
    trial: 'Where the trial\'s recipients come from. "Global" means the trial uses the global defaults; "Custom · 1 to, 2 cc" means the trial sets its own list; "Not sent" switches the email off for this trial. Hover to see the chain of levels.',
    site: 'Where this site\'s recipients come from. "Trial" or "Global" means the site follows that level\'s list; "Custom · 1 to" means the site sets its own; "Not sent" switches the email off for this site. Hover to see the chain of levels.',
    participant:
        'Where this participant\'s recipients come from. "Site", "Trial" or "Global" means the participant follows that level; "Custom" means the participant has their own list; "Not sent" switches the email off for this participant.',
    caseReport:
        'Where this case report\'s recipients come from. It follows the participant or site unless a custom list is set here; "Not sent" switches the email off for this case report only.',
    institution:
        'Where this institution\'s recipients come from. "Global" means it follows the global defaults; "Custom · 1 to" means the institution sets its own list; "Not sent" switches the email off for this institution\'s forms unless a form turns it back on.',
    record: 'Where this record\'s recipients come from. It follows the defaults above it unless a custom list is set here; "Not sent" switches the email off for this record only.'
};

const parentList: Record<EmailLevel, string> = {
    global: "",
    trial: "the global defaults",
    site: "the trial's list",
    participant: "the site's list",
    caseReport: "the participant's or site's list",
    institution: "the global defaults",
    record: "the defaults above this record"
};

/**
 * The email recipients page of a level.
 *
 * Postconditions: The stops walk the shared email panel from search to one email's badges and its
 * editor, described for the level's inheritance.
 */
export const emailRecipientsGuide = (
    level: EmailLevel,
    intro: string,
    updatePermission: Permission,
    extraStops: PageGuideStop[] = []
): PageGuide => ({
    intro,
    stops: [
        stop(
            emailTourTargets.search,
            "Finding an email",
            "Type part of an email's name or description. Every category with a match opens while you search; clear the box to see all emails again."
        ),
        stop(
            emailTourTargets.category,
            "Email categories",
            `Emails are grouped by what they are about. The heading counts the email types in the category${level === "global" ? "" : " and how many are customised here"}. "Cc and Bcc only" means the email's To is decided by what it is about, so only copies can be set.`
        ),
        stop(
            emailTourTargets.email,
            "One email",
            "Each row is one email the system sends: its name, what triggers it, and a badge naming the system that sends it."
        ),
        stop(emailTourTargets.recipients, "Who receives it", recipientStatus[level]),
        stop(
            emailTourTargets.from,
            "From",
            "The name the email appears to come from, and the level that set it. Hover to see the full address."
        ),
        stop(
            emailTourTargets.fallback,
            "Fallback",
            'Who is added when a slot would otherwise be empty, for example when a site has no data manager. "Fallback: if no To" shows the condition. The badge appears only when this level sets its own fallback.'
        ),
        stop(
            emailTourTargets.edit,
            "Changing an email",
            level === "global"
                ? "Edit opens the email's settings: its recipients (roles, named people or addresses, as To, Cc or Bcc) or Don't send, its From identity and its fallback. A preview shows who would receive it. Save or cancel before opening another email."
                : `Edit opens the email's settings: use ${parentList[level]}, set custom recipients (roles, named people or addresses, as To, Cc or Bcc) or Don't send, and optionally a fallback. A preview shows who would receive it. Save or cancel before opening another email.`,
            updatePermission
        ),
        ...extraStops
    ],
    notes: [
        "Levels run from global to trial, site, participant and the record itself, such as a case report or item. Each level follows the one above it until it sets its own recipients, template or timing, and a more specific level wins.",
        "Emails that use a template also let you choose which template is sent, and the overdue reminder lets you choose how many days after a form is created it is sent. Each setting follows the level above it until you change it here.",
        "Switching an email off here does not stop a more specific level from turning it back on for itself."
    ]
});

/**
 * The data entry page of an item or form.
 *
 * Postconditions: The stops walk a section's header, its completion count, N/A and assignment,
 * a question, and the save buttons; the tour opens a collapsed section to show its question.
 */
export const entryGuide = (
    what: string,
    updatePermission: Permission,
    detailsName = "Details"
): PageGuide => ({
    intro: `Record the data collected for ${what}. Earlier versions open read-only; only the current version can be changed.`,
    stops: [
        stop(
            helpTourTargets.pageActions,
            detailsName,
            `${detailsName} returns to ${what}'s details, where its versions are listed and a new version can be created.`
        ),
        stop(
            entryTourTargets.section,
            "Sections",
            "Questions are grouped into sections. Select a section's heading to open or close it; a red heading means a question in it has a problem."
        ),
        stop(
            entryTourTargets.summary,
            "Progress",
            "The count shows answered questions out of the total in the section, and the number of problems when there are any. Use it to see what is left before submitting."
        ),
        stop(
            entryTourTargets.na,
            "Not applicable",
            "Switch on N/A when a whole section does not apply. Its questions are then not required, and the section closes."
        ),
        stop(
            entryTourTargets.assignment,
            "Assignment",
            "Some sections can be assigned to a person or a role, so it is clear who should complete them."
        ),
        stop(
            entryTourTargets.question,
            "Questions",
            "Each row is one question. A marker beside the label shows when an answer is required; some questions are enabled or required only after an earlier answer."
        ),
        stop(
            entryTourTargets.actions,
            "Saving",
            "Save Draft keeps your answers without checking them, so you can finish later. Submit checks every answer: problems that block saving stop it, and others are saved and listed as warnings.",
            updatePermission
        )
    ],
    notes: [
        "If attachments could not be stored, the save succeeds with a warning naming the documents that need attention.",
        "Leaving the page with unsaved answers loses them; save a draft first."
    ]
});

/**
 * The version fields shared by item and form details pages.
 *
 * Postconditions: The version stop explains creating a version; callers add the template and
 * owner fields before these.
 */
export const versionStops = (createsVersion = true): PageGuideStop[] => [
    field(
        "currentVersion.version",
        "Version",
        createsVersion
            ? "The current version's name. Create Version starts a new version with its own data entry; earlier versions stay readable. Finish the current version's data entry first, because creating a version discards unsaved changes."
            : "The current version's name. Earlier versions stay readable from the version list."
    ),
    field(
        "currentVersion.versionDate",
        "Version Date",
        "The date the current version applies from, such as the date an agreement was signed. It is shown in the version list."
    ),
    field(
        "currentVersion.comments",
        "Comments",
        "Why this version exists or anything a reviewer should know about it, such as what changed from the last version."
    ),
    field(
        "sortOrderId",
        "Sort Versions",
        "How the version history is ordered: Default, or by version date or version name, ascending or descending."
    )
];

/**
 * The participant form, shared by the trial's and a site's participant pages.
 *
 * Postconditions: The stops follow the form's order; the side menu stop is included on the
 * details page only.
 */
export const participantGuide = (updatePermission: Permission, withMenu: boolean): PageGuide => ({
    intro: "A participant's registration on the trial: who they are, where they were registered, their treatment, and the people responsible for them. Participants linked to OpenClinica are kept in step with it.",
    stops: [
        field(
            "institutionId",
            "Institution",
            "The site the participant was registered at. It decides which site's staff can see the participant, and cannot be changed once the participant is saved."
        ),
        field(
            "initials",
            "Participant Initials",
            "The participant's initials, as written on the registration form. Initials, not names, keep the participant de-identified."
        ),
        field(
            "registrationNumber",
            "Participant Reg No.",
            "The trial registration (study) number given at registration. It identifies the participant everywhere else in the system, such as in emails and case reports."
        ),
        field(
            "caseTypeId",
            "Case Type",
            "The kind of case, from the case types maintained in settings. It is shown in participant lists and can be used to filter them."
        ),
        field(
            "trialTreatmentId",
            "Treatment",
            "The randomisation arm the participant was allocated to, from the trial's treatment arms."
        ),
        field(
            "accrualDate",
            "Accrual Date",
            "When the participant was registered. It counts towards the site's accrual and the trial's accrual reports."
        ),
        field(
            "treatingClinicianId",
            "Treating Clinician",
            "The clinician treating the participant, chosen from people with a clinician role at the site on this trial. Emails about the participant can be sent to them."
        ),
        field(
            "dataManagerId",
            "QA Data Manager",
            "The data manager responsible for the participant's QA, chosen from people with a data manager role at the site on this trial."
        ),
        field(
            "patientTagAssignments",
            "Participant Tags",
            "Tags that classify the participant, from the tags the trial uses. Tags can decide which participant items apply. Shown only when the trial uses participant tags."
        ),
        field(
            "active",
            "Active",
            "Clear to withdraw the participant from lists. Their records are kept."
        ),
        saveStop(
            "Submit saves the participant. Participant items that now apply, based on their tags, are created, and items that no longer apply are made inactive.",
            updatePermission
        ),
        ...(withMenu
            ? [
                  menu(
                      item("Details", "This page: the participant's registration details."),
                      item(
                          "Administration Case Report",
                          "The participant's QA administration case report, combining the QA forms completed for them."
                      ),
                      item(
                          "Items",
                          "The items collected for the participant, such as consent and eligibility records, with their versions."
                      ),
                      item(
                          "Emails",
                          "Who receives the emails sent about this participant, overriding the site's recipients if needed."
                      )
                  )
              ]
            : [])
    ],
    rules: [
        "Institution, Participant Initials and Participant Reg No. are required.",
        "When the participant is linked to an OpenClinica subject, their identity, treatment, accrual date and active state come from OpenClinica and cannot be changed here."
    ]
});

/** The columns of a participants list; the trial's list also shows the site. */
export const participantColumns = (withSite: boolean) => [
    column(
        "Registration No.",
        "The trial registration (study) number, which identifies the participant everywhere else."
    ),
    column("Initials", "The participant's initials, as on the registration form."),
    column("Case Type", "The kind of case, from the case types maintained in settings."),
    column("Treatment", "The randomisation arm the participant was allocated to."),
    ...(withSite
        ? [
              column("Institution", "The site the participant was registered at."),
              column("Code", "The site's institution code, for quick recognition.")
          ]
        : [])
];

/**
 * The layout builder of a template: its actions, then a section, a column and a component, from
 * the controls that manage them.
 *
 * Postconditions: The layout field itself is described for the help text; the tour visits the
 * builder's controls, which exist once the layout has a section.
 */
export const builderStops = (updatePermission: Permission): PageGuideStop[] => [
    field(
        "collection",
        "Layout",
        "What the template collects, arranged as sections of questions in columns. The steps that follow show how to build it; the preview shows it as people will complete it."
    ),
    stop(
        helpTourTargets.pageActions,
        "Layout actions",
        "Add Section appends a section to the layout. Preview shows the entry form as people will complete it. Lock stops further changes once the template is final (save first); Unlock needs the collection unlock permission.",
        updatePermission
    ),
    stop(
        builderTourTargets.showInactive,
        "Show inactive",
        "Show sections and components that have been deactivated, so they can be checked or reactivated. Deactivated parts are kept for records already collected."
    ),
    stop(
        insertionTourTarget("Add section here"),
        "Inserting a section",
        "The + between sections inserts a new section at that point, rather than at the end."
    ),
    stop(
        builderTourTargets.sectionTools,
        "Section tools",
        "Add a component to the section, manage its columns, move it up or down, edit its settings, or deactivate it."
    ),
    stop(
        builderTourTargets.columnTools,
        "Column tools",
        "When a section has several columns, each column has its own tools to add a component or move the column left or right."
    ),
    stop(
        insertionTourTarget("Add component here"),
        "Inserting a component",
        "The + between components inserts a component at that point. Components are chosen from the component library."
    ),
    stop(
        builderTourTargets.itemTools,
        "Component tools",
        "Move a component up, down or between columns, edit its placement, or deactivate it."
    ),
    stop(
        entryTourTargets.question,
        "Questions",
        "Each question appears as it will in data entry, including whether it is required and the options it offers."
    )
];
